import React, { useState, useEffect, useContext } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import { CustomRole } from '../../../domain/usecases/Roles'
import { PermissionsSelector } from '../UserManagement/PermissionsSelector'
import { RemoteUpdateCustomRole } from '../../../data/usecases/Roles/RemoteUpdateCustomRole'
import { RemoteCreateCustomRole } from '../../../data/usecases/Roles/RemoteCreateCustomRole'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'
import { throwIfHttpError } from '../httpError'
import { AppContext } from '../../../app'

interface EditCustomRoleModalProps {
  show: boolean
  onHide: () => void
  onSuccess: () => void
  role: CustomRole | null
}

export const EditCustomRoleModal: React.FC<EditCustomRoleModalProps> = ({
  show,
  onHide,
  onSuccess,
  role
}) => {
  const { user } = useContext(AppContext)
  const httpClient = useAxiosHttpClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  const [displayName, setDisplayName] = useState(role?.display_name || '')
  const [description, setDescription] = useState(role?.description || '')

  const isEditMode = role !== null
  const companyId = user?.selectedContext?.companyId

  // Load current permissions when modal opens
  useEffect(() => {
    if (show) {
      if (role) {
        // Edit mode: load existing permissions
        loadRolePermissions()
        setDisplayName(role.display_name)
        setDescription(role.description || '')
      } else {
        // Create mode: reset form
        setDisplayName('')
        setDescription('')
        setSelectedPermissions([])
      }
    }
  }, [show, role])

  const loadRolePermissions = async () => {
    if (!role) return

    try {
      const response = await httpClient.get?.({
        url: `${URL_API}/permissions/role/${role.id}?isSystemRole=false`
      })

      console.log('Role permissions response:', response)

      // Backend returns array of permission strings directly
      if (response && Array.isArray(response)) {
        console.log('Setting permissions:', response)
        setSelectedPermissions(response)
      } else if (
        response &&
        response.permissions &&
        Array.isArray(response.permissions)
      ) {
        // Fallback: handle object format if structure changes
        const permissionKeys = response.permissions.map(
          (p: any) => `${p.resource}.${p.action}`
        )
        console.log('Mapped permission keys:', permissionKeys)
        setSelectedPermissions(permissionKeys)
      } else {
        console.log('No permissions found in response')
      }
    } catch (err) {
      console.error('Error loading role permissions:', err)
      setError('Erro ao carregar permissões da função')
    }
  }

  const generateRoleName = (displayName: string): string => {
    // Convert display name to machine-readable name (slug)
    // "Gerente de Vendas" -> "gerente_de_vendas"
    return displayName
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s]/g, '') // Remove special chars
      .replace(/\s+/g, '_') // Replace spaces with underscores
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!displayName.trim()) {
      setError('Por favor, informe o nome da função')
      return
    }

    if (selectedPermissions.length === 0) {
      setError('Por favor, selecione pelo menos uma permissão')
      return
    }

    if (!companyId) {
      setError('Empresa não encontrada no contexto')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const roleData = {
        display_name: displayName,
        description: description,
        permissions: selectedPermissions
      }

      if (isEditMode) {
        // Edit existing role
        const updateRoleUseCase = new RemoteUpdateCustomRole(
          URL_API || '',
          httpClient
        )

        console.log('Updating role with data:', roleData)
        const result = await updateRoleUseCase.update(role!.id, roleData)
        console.log('Update result:', result)
        throwIfHttpError(result, 'Erro ao atualizar função')
      } else {
        // Create new role
        const createRoleUseCase = new RemoteCreateCustomRole(
          URL_API || '',
          httpClient
        )

        const createData = {
          name: generateRoleName(displayName),
          ...roleData,
          company_id: companyId
        }

        console.log('Creating role with data:', createData)
        const result = await createRoleUseCase.create(createData)
        console.log('Create result:', result)
        throwIfHttpError(result, 'Erro ao criar função')
      }

      onSuccess()
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} role:`, err)
      console.error('Error details:', err.response || err.message)
      setError(
        err.message || `Erro ao ${isEditMode ? 'atualizar' : 'criar'} função`
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode
              ? 'Editar Função Personalizada'
              : 'Criar Função Personalizada'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form.Group>
            <Form.Label>Nome da Função *</Form.Label>
            <Form.Control
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Ex: Gerente de Vendas"
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição opcional da função"
            />
          </Form.Group>

          <PermissionsSelector
            selectedPermissions={selectedPermissions}
            onChange={setSelectedPermissions}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                {isEditMode ? 'Salvando...' : 'Criando...'}
              </>
            ) : (
              <>
                <i
                  className={`feather icon-${
                    isEditMode ? 'save' : 'plus'
                  } mr-2`}
                />
                {isEditMode ? 'Salvar Alterações' : 'Criar Função'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
